import React, { useState } from 'react';
import { Settings, Eye, User, Clock, Brain, RefreshCw } from 'lucide-react';

/**
 * Developer Testing Panel for Intelligence Dashboard
 * 
 * Provides tools for simulating different user contexts, time periods,
 * and family scenarios to test the contextual intelligence system.
 * Only visible in development mode.
 */
export default function DeveloperTestingPanel({ context, onContextOverride, onRefresh }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [overrides, setOverrides] = useState({});

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const simulateTimeOfDay = (period) => {
    const timeOverrides = {
      'morning': { hour: 8, period: 'morning', energy: 'rising', focus: 'planning' },
      'mid-morning': { hour: 10, period: 'mid-morning', energy: 'high', focus: 'productive' },
      'lunch': { hour: 13, period: 'lunch', energy: 'moderate', focus: 'social' },
      'afternoon': { hour: 15, period: 'afternoon', energy: 'steady', focus: 'execution' },
      'early-evening': { hour: 18, period: 'early-evening', energy: 'winding-down', focus: 'transition' },
      'evening': { hour: 20, period: 'evening', energy: 'social', focus: 'family-time' },
      'late-night': { hour: 23, period: 'late-night', energy: 'low', focus: 'rest' }
    };

    setOverrides(prev => ({
      ...prev,
      temporal: timeOverrides[period]
    }));

    onContextOverride({
      ...context,
      temporal: { ...context.temporal, ...timeOverrides[period] }
    });
  };

  const simulateUserType = (userType) => {
    const userOverrides = {
      'high-achiever-teen': {
        productivityPeaks: ['morning', 'afternoon'],
        completionRate: 0.9,
        energyPattern: 'consistent'
      },
      'evening-person-teen': {
        productivityPeaks: ['evening', 'late-night'],
        completionRate: 0.7,
        energyPattern: 'late-start'
      },
      'busy-parent': {
        productivityPeaks: ['early-morning', 'late-evening'],
        completionRate: 0.85,
        energyPattern: 'sporadic'
      },
      'stressed-student': {
        productivityPeaks: ['afternoon'],
        completionRate: 0.6,
        energyPattern: 'inconsistent'
      }
    };

    setOverrides(prev => ({
      ...prev,
      personal: userOverrides[userType]
    }));

    onContextOverride({
      ...context,
      personal: { ...context.personal, ...userOverrides[userType] }
    });
  };

  const simulateAcademicLoad = (load) => {
    const academicOverrides = {
      'low': { academicLoad: 'low', stressLevel: 'low', upcomingDeadlines: [] },
      'moderate': { 
        academicLoad: 'moderate', 
        stressLevel: 'moderate', 
        upcomingDeadlines: [
          { title: 'History Essay', date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000) },
          { title: 'Math Quiz', date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }
        ]
      },
      'high': { 
        academicLoad: 'high', 
        stressLevel: 'high', 
        upcomingDeadlines: [
          { title: 'Science Project', date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000) },
          { title: 'English Paper', date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) },
          { title: 'Math Test', date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) },
          { title: 'College Application', date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000) }
        ]
      }
    };

    setOverrides(prev => ({
      ...prev,
      academic: academicOverrides[load]
    }));

    onContextOverride({
      ...context,
      academic: { ...context.academic, ...academicOverrides[load] }
    });
  };

  const clearOverrides = () => {
    setOverrides({});
    onRefresh();
  };

  if (!isExpanded) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-indigo-600 text-white p-3 rounded-full shadow-lg hover:bg-indigo-700 transition-colors"
          title="Open Developer Testing Panel"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-xl border border-stone-200 p-4 w-80 max-h-96 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-stone-900">Dev Testing</h3>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-stone-400 hover:text-stone-600"
        >
          ×
        </button>
      </div>

      <div className="space-y-4">
        {/* Time Simulation */}
        <div>
          <h4 className="font-medium text-stone-800 mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Time Simulation
          </h4>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {['morning', 'mid-morning', 'lunch', 'afternoon', 'early-evening', 'evening'].map(period => (
              <button
                key={period}
                onClick={() => simulateTimeOfDay(period)}
                className={`p-2 rounded border text-left ${
                  overrides.temporal?.period === period
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-800'
                    : 'bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100'
                }`}
              >
                {period.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* User Type Simulation */}
        <div>
          <h4 className="font-medium text-stone-800 mb-2 flex items-center gap-2">
            <User className="w-4 h-4" />
            User Profiles
          </h4>
          <div className="space-y-1 text-xs">
            {[
              { key: 'high-achiever-teen', label: 'High Achiever Teen' },
              { key: 'evening-person-teen', label: 'Evening Person Teen' },
              { key: 'busy-parent', label: 'Busy Parent' },
              { key: 'stressed-student', label: 'Stressed Student' }
            ].map(user => (
              <button
                key={user.key}
                onClick={() => simulateUserType(user.key)}
                className={`w-full p-2 rounded border text-left ${
                  overrides.personal?.energyPattern
                    ? 'bg-violet-100 border-violet-300 text-violet-800'
                    : 'bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100'
                }`}
              >
                {user.label}
              </button>
            ))}
          </div>
        </div>

        {/* Academic Load Simulation */}
        <div>
          <h4 className="font-medium text-stone-800 mb-2 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Academic Load
          </h4>
          <div className="grid grid-cols-3 gap-1 text-xs">
            {['low', 'moderate', 'high'].map(load => (
              <button
                key={load}
                onClick={() => simulateAcademicLoad(load)}
                className={`p-2 rounded border text-center ${
                  overrides.academic?.academicLoad === load
                    ? 'bg-amber-100 border-amber-300 text-amber-800'
                    : 'bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100'
                }`}
              >
                {load}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-stone-200">
          <button
            onClick={onRefresh}
            className="flex-1 bg-indigo-600 text-white px-3 py-2 rounded text-xs hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
          <button
            onClick={clearOverrides}
            className="flex-1 bg-stone-600 text-white px-3 py-2 rounded text-xs hover:bg-stone-700 transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Active Overrides Display */}
        {Object.keys(overrides).length > 0 && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
            <div className="font-medium text-amber-800 mb-1">Active Overrides:</div>
            <div className="text-amber-700">
              {Object.keys(overrides).map(key => (
                <div key={key}>• {key}: {JSON.stringify(overrides[key], null, 0).substring(0, 50)}...</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}